/**
 * Event Catalog admin page (GDN-2681).
 *
 * Master-detail over the event-types catalog: every event type the system
 * has registered in code OR observed in the analytics store, with the
 * code-owned declaration mirrors (read-only), per-tier observed stats,
 * human-curated metadata (editable by admins), and the tag memberships
 * that drive tag-filtered activity surfaces. Retagging here reshapes
 * those surfaces retroactively — the catalog resolves at query time.
 *
 * Drift is a first-class view, not a report: observed-only rows (in
 * data, never registered), orphaned rows (vanished from code), and
 * silent rows (registered, no data in 180d) each get a badge and a
 * filter.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { EventCatalogDetailPanel } from '../components/event-catalog-detail-panel';
import type { EventCatalogEntry } from '../store/api';
import {
  useListEventCatalogQuery,
  useListEventTagsQuery,
  useSyncEventCatalogMutation,
} from '../store/hooks';

const STALE_MS = 180 * 24 * 60 * 60 * 1000;

type DriftFilter = 'all' | 'observed-only' | 'orphaned' | 'uncurated' | 'silent';

export const isSilent = (entry: EventCatalogEntry): boolean =>
  !!entry.last_seen_in_code_at &&
  (!entry.last_seen_in_data_at ||
    Date.now() - new Date(entry.last_seen_in_data_at).getTime() > STALE_MS);

const isUncurated = (entry: EventCatalogEntry): boolean =>
  !entry.display_label && !entry.description && !entry.category && !(entry.tags ?? []).length;

export const driftBadges = (entry: EventCatalogEntry): { label: string; title: string }[] => {
  const badges: { label: string; title: string }[] = [];
  if (entry.observed_only)
    badges.push({
      label: 'observed-only',
      title: 'Seen in the analytics store but never registered in code.',
    });
  if (entry.status === 'orphaned')
    badges.push({ label: 'orphaned', title: 'Vanished from the code registry (never deleted).' });
  if (entry.status === 'deprecated')
    badges.push({ label: 'deprecated', title: `Superseded${entry.superseded_by ? ` by ${entry.superseded_by}` : ''}.` });
  if (isSilent(entry))
    badges.push({ label: 'silent', title: 'Registered in code but no events observed in 180 days.' });
  return badges;
};

export default function EventCatalogPage() {
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [drift, setDrift] = useState<DriftFilter>('all');
  const [moduleFilter, setModuleFilter] = useState('');
  // Selection lives in the URL so rows are shareable/back-buttonable.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedType = searchParams.get('type');

  const { data, isLoading, error, refetch } = useListEventCatalogQuery({});
  const { data: tagData } = useListEventTagsQuery();
  const [forceSync, { isLoading: syncing }] = useSyncEventCatalogMutation();
  const [syncError, setSyncError] = useState<string | null>(null);

  // Sync freshness, derived from the data itself (no meta endpoint):
  // every registry-sync run refreshes last_seen_in_code_at on registered
  // rows, and every sweep stamps swept_at per tier — so the maxima ARE
  // the last run times.
  const lastRuns = useMemo(() => {
    let syncAt: number | null = null;
    let sweepAt: number | null = null;
    for (const entry of data?.entries ?? []) {
      if (entry.last_seen_in_code_at) {
        const at = new Date(entry.last_seen_in_code_at).getTime();
        if (!Number.isNaN(at)) syncAt = Math.max(syncAt ?? 0, at);
      }
      for (const stats of Object.values(entry.observed_stats ?? {})) {
        const sweptAt = (stats as { swept_at?: string }).swept_at;
        if (sweptAt) {
          const at = new Date(sweptAt).getTime();
          if (!Number.isNaN(at)) sweepAt = Math.max(sweepAt ?? 0, at);
        }
      }
    }
    return { syncAt, sweepAt };
  }, [data]);

  const entries = useMemo(() => {
    let all = data?.entries ?? [];
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      all = all.filter(
        entry =>
          entry.event_type.toLowerCase().includes(needle) ||
          (entry.display_label ?? '').toLowerCase().includes(needle) ||
          (entry.description ?? '').toLowerCase().includes(needle),
      );
    }
    if (tagFilter) all = all.filter(entry => (entry.tags ?? []).includes(tagFilter));
    if (moduleFilter) all = all.filter(entry => entry.source_module === moduleFilter);
    if (drift === 'observed-only') all = all.filter(entry => entry.observed_only);
    if (drift === 'orphaned') all = all.filter(entry => entry.status === 'orphaned');
    if (drift === 'uncurated') all = all.filter(isUncurated);
    if (drift === 'silent') all = all.filter(isSilent);
    return all;
  }, [data, search, tagFilter, moduleFilter, drift]);

  const modules = useMemo(
    () =>
      [
        ...new Set(
          (data?.entries ?? [])
            .map(entry => entry.source_module)
            .filter((module): module is string => !!module),
        ),
      ].sort(),
    [data],
  );

  return (
    <div className="min-h-full min-w-0 bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <Box>
            <h1 className="text-2xl font-bold text-gray-900">Event Catalog</h1>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Every event type registered in code or observed in the analytics
              store. Tags drive the activity timelines&apos; filters — edits
              apply retroactively over all history.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search types…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <TextField
              select
              size="small"
              label="Tag"
              value={tagFilter}
              onChange={e => setTagFilter(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="">All</MenuItem>
              {(tagData?.tags ?? []).map(tag => (
                <MenuItem key={tag.tag_name} value={tag.tag_name}>
                  {tag.tag_name} ({tag.member_count})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Module"
              value={moduleFilter}
              onChange={e => setModuleFilter(e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">All</MenuItem>
              {modules.map(module => (
                <MenuItem key={module} value={module}>
                  {module}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Show"
              value={drift}
              onChange={e => setDrift(e.target.value as DriftFilter)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="uncurated">Uncurated</MenuItem>
              <MenuItem value="observed-only">Observed-only</MenuItem>
              <MenuItem value="orphaned">Orphaned</MenuItem>
              <MenuItem value="silent">Silent (180d)</MenuItem>
            </TextField>
            {(lastRuns.syncAt || lastRuns.sweepAt) && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  synced{' '}
                  {lastRuns.syncAt ? new Date(lastRuns.syncAt).toLocaleString() : 'never'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  swept{' '}
                  {lastRuns.sweepAt ? new Date(lastRuns.sweepAt).toLocaleString() : 'never'}
                </Typography>
              </Box>
            )}
            <Tooltip title="Run the registry sync + observed sweep now instead of waiting for the schedules.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                  disabled={syncing}
                  onClick={() => {
                    setSyncError(null);
                    void forceSync()
                      .unwrap()
                      .then(refetch)
                      .catch(() => setSyncError('Sync failed — check the API logs.'));
                  }}
                >
                  Sync now
                </Button>
              </span>
            </Tooltip>
          </Box>
        </div>

        {!!error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load the event catalog. Try refreshing.
          </Alert>
        )}
        {syncError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSyncError(null)}>
            {syncError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Paper
            sx={{
              overflow: 'hidden',
              // Full width until a type is selected; the detail panel
              // claims its share only while open.
              flex: selectedType ? '0 1 460px' : '1 1 100%',
              minWidth: 340,
            }}
          >
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table size="small" stickyHeader aria-label="Event types">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Event type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">
                            Loading catalog…
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && entries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {search || tagFilter || drift !== 'all'
                            ? 'No types match the filters.'
                            : 'The catalog is empty — run a sync.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {entries.map(entry => (
                    <TableRow
                      key={entry.event_type}
                      hover
                      selected={entry.event_type === selectedType}
                      onClick={() => setSearchParams({ type: entry.event_type })}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.78rem' }}
                        >
                          {entry.event_type}
                        </Typography>
                        {entry.display_label && (
                          <Typography variant="caption" color="text.secondary">
                            {entry.display_label}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                        >
                          {entry.source_module ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(entry.tags ?? []).map(tag => (
                            <Chip key={tag} size="xSmall" variant="soft" color="info" label={tag} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {driftBadges(entry).map(badge => (
                            <Tooltip key={badge.label} title={badge.title}>
                              <Chip
                                size="xSmall"
                                variant="soft"
                                color={badge.label === 'silent' ? 'warning' : 'default'}
                                label={badge.label}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {selectedType && (
            <Box sx={{ flex: '1 1 480px', minWidth: 360 }}>
              <EventCatalogDetailPanel
                key={selectedType}
                eventType={selectedType}
                onClose={() => setSearchParams({})}
              />
            </Box>
          )}
        </Box>
      </main>
    </div>
  );
}
